/**
 * 🎯 TechIcon Component - Modern Technology Icons System
 * 
 * A centralized component for displaying technology icons using React Icons.
 * Now integrated with Supabase for dynamic technology data management.
 * 
 * Features:
 * - ⚛️ React Icons integration for authentic brand icons
 * - 🗄️ Supabase integration for centralized data
 * - 🎨 Official brand colors for each technology
 * - 📱 Responsive sizing and scaling
 * - ✨ Hover animations and effects
 * - 🔧 TypeScript support with full type safety
 * - 🎯 Intelligent fallback system for unknown technologies
 * - 🌐 Bilingual support (French/English)
 * - 🎨 Extended icon library with 400+ icons
 * 
 */
import React, { useState } from 'react';
import { useTechIcons } from '../../hooks/useTechIcons';
import * as styles from './techIcon.css';
import { resolveIcon } from './iconRegistry';
import { customIconsService, type CustomIcon } from '../../services/customIconsService';

/**
 * 🎯 TechIcon Component Props Interface
 * 
 * Defines the props for the TechIcon component with comprehensive type safety.
 * All props are optional except 'type' to ensure flexible usage.
 */
export interface TechIconProps {
  /** Technology name/type (e.g., 'react', 'typescript', 'python') */
  type: string;
  /** Icon size in pixels (default: 24px) */
  size?: number;
  /** Custom color override (optional, uses brand color by default) */
  color?: string;
  /** Additional CSS class names for custom styling */
  className?: string;
  /** Accessibility title for screen readers */
  title?: string;
  /** Show loading state while fetching data */
  showLoading?: boolean;
}

/**
 * TechIcon component with extended icon support
 */
export const TechIcon: React.FC<TechIconProps> = ({
  type,
  size = 24,
  color,
  className = '',
  title,
  showLoading = false
}) => {
  const { getTechWithIcon, loading, error } = useTechIcons();
  const [imgFailed, setImgFailed] = useState(false);

  const isProbablyUrl = (value?: string): boolean => {
    if (!value) return false;
    const v = value.trim().toLowerCase();
    return (
      v.startsWith('http://') ||
      v.startsWith('https://') ||
      v.startsWith('//') ||
      v.startsWith('data:image') ||
  v.startsWith('/') ||           // relative paths (e.g., /storage/...)
  v.startsWith('storage/')       // supabase paths without leading slash
    );
  };

  const resolveImageUrl = (raw?: string): string | undefined => {
    if (!raw) return undefined;
    const base = (import.meta as any)?.env?.VITE_API_URL as string | undefined;
    const trimmed = raw.trim();
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('//') || trimmed.startsWith('data:image')) {
      return trimmed;
    }
    if (trimmed.startsWith('/')) {
      if (base) {
        return `${base.replace(/\/$/, '')}${trimmed}`;
      }
  return trimmed; // leave as-is if no base URL
    }
    if (trimmed.startsWith('storage/')) {
      if (base) {
        return `${base.replace(/\/$/, '')}/${trimmed}`;
      }
      return `/${trimmed}`;
    }
    return trimmed;
  };

  // Show loading state if requested
  if (loading && showLoading) {
    return (
      <div 
        className={`${styles.loadingContainer} ${className}`}
        style={{ width: size, height: size }}
        title="Chargement..."
      >
        <div className={styles.loadingSpinner} />
      </div>
    );
  }

  const normalize = (v?: string) => (v || '')
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const slugify = (v?: string) => normalize(v).replace(/\s+/g, '');

  const key = type.toLowerCase();
  const resolved = resolveIcon(slugify(type));

  // Get technology data for potential custom image
  const techData = getTechWithIcon(type) || getTechWithIcon(slugify(type)) || getTechWithIcon(normalize(type));
  const techAny = techData as any;
  // Find a custom icon (uploaded via admin) by key
  const customIcon: CustomIcon | undefined = (() => {
    try {
      const list = customIconsService.getCustomIcons();
      const target = slugify(type);
      return list.find(ic => slugify(ic.key) === target);
    } catch {
      return undefined;
    }
  })();
  const imageUrlCandidate: string | undefined =
    techAny?.icon_url ||
    techAny?.image_url ||
    techAny?.logo_url ||
    techAny?.logo ||
    techAny?.image ||
    (typeof techAny?.icon_name === 'string' && isProbablyUrl(techAny?.icon_name) ? techAny?.icon_name : undefined) ||
    techAny?.iconUrl ||
    techAny?.imageUrl ||
    techAny?.url;

  // 0) If "type" is directly a hosted image URL, render it
  if (!imgFailed && isProbablyUrl(type)) {
    const src = resolveImageUrl(type)!;
    return (
      <img
        src={src}
        alt={title || key}
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        className={className}
        style={{ width: size, height: size, objectFit: 'contain', borderRadius: 6 }}
        onError={() => setImgFailed(true)}
      />
    );
  }

  // 0.5) If an uploaded custom icon exists for this key, display it
  if (!imgFailed && customIcon && isProbablyUrl(customIcon.url)) {
    const src = resolveImageUrl(customIcon.url)!;
    return (
      <img
        src={src}
        alt={title || customIcon.name || key}
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        className={className}
        style={{ width: size, height: size, objectFit: 'contain', borderRadius: 6 }}
        onError={() => setImgFailed(true)}
      />
    );
  }

  // 1) If an image URL is available for this technology, render it first
  if (!imgFailed && imageUrlCandidate && isProbablyUrl(imageUrlCandidate)) {
    const src = resolveImageUrl(imageUrlCandidate)!;
    return (
      <img
        src={src}
        alt={title || techData?.displayName || key}
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        className={className}
        style={{ width: size, height: size, objectFit: 'contain', borderRadius: 6 }}
        onError={() => setImgFailed(true)}
      />
    );
  }

  if (resolved.hasComponent) {
    const IconC = resolved.IconComponent;
    return (
      <IconC
        size={size}
        color={color || resolved.color}
        className={`${styles.techIcon} ${className}`}
        title={title || resolved.name}
        aria-hidden="true"
      />
    );
  }

  // 2) Otherwise, try the React icon resolved via database
  if (techData?.icon) {
    const IconComponent = techData.icon;
    return (
      <IconComponent
        size={size}
        color={color || techData.color}
        className={`${styles.techIcon} ${className}`}
        title={title || techData.displayName}
        aria-hidden="true"
      />
    );
  }

  // Final fallback: stylized initial letter
  const letter = key ? key[0].toUpperCase() : '?';
  return (
    <div
      className={`${styles.fallbackIcon} ${className}`}
      style={{
        width: size,
        height: size,
        fontSize: size * 0.55,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f1f5f9',
        borderRadius: 6,
        color: color || resolved.color || '#6b7280',
        fontWeight: 600,
        border: '1px solid #d1d5db'
      }}
      title={title || `${type} (fallback)`}
    >
      {letter}
    </div>
  );
};

/**
 * Hook to get a technology color from Supabase
 */
export const useTechColor = (type: string): string => {
  const { getTechWithIcon } = useTechIcons();
  const techData = getTechWithIcon(type);
  return techData?.color || '#6b7280';
};

/**
 * Utility function to check if a technology exists
 */
export const hasTechIcon = (type: string): boolean => !!resolveIcon(type.toLowerCase()).hasComponent;

export default TechIcon;
